import axios, { AxiosInstance } from "axios";
import { GetTokenSilentlyOptions } from "@auth0/auth0-react";
import { GetTokenSilentlyVerboseResponse } from "@auth0/auth0-spa-js/dist/typings/global";
import { fetchMyCompany } from "./fetchMyCompany/fetchMyCompanyApi";
import { extractErrorMessage } from "@/utils/extractErrorMessage";

declare module "axios" {
  interface AxiosRequestConfig {
    /** Optional decoration (streaks, hints): fail quietly, no global error toast. */
    silent?: boolean;
  }
}

const axiosInstance: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

export const axiosInstanceWithoutInterceptors: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

let accessTokenInterceptorAdded = false;

export const addAccessTokenInterceptor = (
  axiosInstance: AxiosInstance,
  getAccessTokenSilently: {
    (
      options: GetTokenSilentlyOptions & { detailedResponse: true },
    ): Promise<GetTokenSilentlyVerboseResponse>;
    (options?: GetTokenSilentlyOptions): Promise<string>;
    (
      options: GetTokenSilentlyOptions,
    ): Promise<GetTokenSilentlyVerboseResponse | string>;
  },
  loginWithRedirect: () => Promise<void>,
  showNotification: (
    message: string,
    description: string,
    type: "success" | "error",
  ) => void,
) => {
  // Prevent adding the interceptor multiple times
  if (accessTokenInterceptorAdded) {
    return;
  }
  accessTokenInterceptorAdded = true;
  axiosInstance.interceptors.request.use(
    async (config) => {
      try {
        console.log("Fetching Auth0 token for request:", config.url);
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          },
        });
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          console.log("Added Auth0 token to request");
        } else {
          console.warn("No Auth0 token available");
        }
      } catch (error) {
        console.error("Error getting Auth0 token:", error);
        console.error("Error getting access token:", error);
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401) {
        console.log("Unauthorized! Redirecting to login...");
        await loginWithRedirect();
      } else if (error.config?.silent) {
        // The caller opted out of the global toast and handles the failure itself.
      } else if (error.response?.status === 403) {
        console.warn("403 Forbidden from API:", error.config?.url);
        // Don't hard-redirect on API 403s — let the component handle the error.
        // The PermissionGuard handles frontend route-level access control.
      } else {
        try {
          const errorMessage = await extractErrorMessage(error.response);
          showNotification("Error", errorMessage, "error");
        } catch (parseError) {
          console.error("Failed to extract error message:", parseError);
          showNotification("Error", "An error occurred", "error");
        }
      }

      return Promise.reject(error);
    },
  );
};

let companyUuidInterceptorAdded = false;

export const addCompanyUuidInterceptor = (
  axiosInstance: AxiosInstance,
  getAccessTokenSilently: {
    (
      options: GetTokenSilentlyOptions & { detailedResponse: true },
    ): Promise<GetTokenSilentlyVerboseResponse>;
    (options?: GetTokenSilentlyOptions): Promise<string>;
    (
      options: GetTokenSilentlyOptions,
    ): Promise<GetTokenSilentlyVerboseResponse | string>;
  },
) => {
  // Prevent adding the interceptor multiple times
  if (companyUuidInterceptorAdded) {
    return;
  }
  companyUuidInterceptorAdded = true;

  const fetchCompanyUuid = async () => {
    console.log("[fetchCompanyUuid] Starting to fetch company UUID...");
    try {
      console.log("[fetchCompanyUuid] Getting access token...");
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        },
      });
      console.log("[fetchCompanyUuid] Access token obtained:", token ? "✓" : "✗");

      if (!token) {
        console.error("[fetchCompanyUuid] No authentication token available");
        return null;
      }

      console.log("[fetchCompanyUuid] Calling /api/private/my-company...");
      const myCompanyResponse = await fetchMyCompany(token);
      console.log("[fetchCompanyUuid] Raw response:", myCompanyResponse);

      const companyUuid = myCompanyResponse.data.uuid;
      console.log("[fetchCompanyUuid] Extracted company UUID:", companyUuid);

      if (companyUuid) {
        console.log("Fetched company UUID from API:", companyUuid);
        localStorage.setItem("selectedCompanyUuid", companyUuid);
        console.log("[fetchCompanyUuid] ✓ Company UUID saved to localStorage:", companyUuid);
        return companyUuid;
      } else {
        console.error("[fetchCompanyUuid] ✗ No company UUID found in response");
        return null;
      }
    } catch (error: any) {
      console.error("[fetchCompanyUuid] ✗ Error fetching company UUID:", error);
      if (error.response?.status === 401) {
        console.error("[fetchCompanyUuid] Authentication failed when fetching company UUID");
      }
      if (error instanceof Error) {
        console.error("[fetchCompanyUuid] Error message:", error.message);
        console.error("[fetchCompanyUuid] Error stack:", error.stack);
      }
      // Clear invalid cached UUID on error
      localStorage.removeItem("selectedCompanyUuid");
      return null;
    }
  };

  axiosInstance.interceptors.request.use(
    async (config) => {
      // Skip adding company UUID header for the my-company endpoint to avoid circular dependency
      if (config.url?.includes('/api/private/my-company')) {
        return config;
      }

      let companyUuid = localStorage.getItem("selectedCompanyUuid");

      if (!companyUuid) {
        companyUuid = await fetchCompanyUuid();
      }

      if (companyUuid) {
        console.log("Setting X-Company-UUID header:", companyUuid, "for request:", config.url);
        config.headers["X-Company-UUID"] = companyUuid;
      }

      if (
        process.env.REACT_APP_API_DEBUG === "true" &&
        config.url &&
        !config.url.includes("XDEBUG_SESSION_START=PHPSTORM")
      ) {
        const separator = config.url.includes("?") ? "&" : "?";
        config.url += `${separator}XDEBUG_SESSION_START=PHPSTORM`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );
};

let impersonateUserInterceptorAdded = false;

export const addImpersonateUserInterceptor = (axiosInstance: AxiosInstance) => {
  // Prevent adding the interceptor multiple times
  if (impersonateUserInterceptorAdded) {
    return;
  }
  impersonateUserInterceptorAdded = true;

  axiosInstance.interceptors.request.use(
    (config) => {
      const impersonateEmployeeUuid = localStorage.getItem("impersonateEmployeeUuid");

      if (impersonateEmployeeUuid) {
        config.headers["X-Impersonate-Employee-UUID"] = impersonateEmployeeUuid;
        console.log(`Impersonating employee: ${impersonateEmployeeUuid}`);
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );
};


export { axiosInstance };
export default axiosInstance;
